// M72 (2026-04-25) — Admin Cheat Log page.
// Xem các attempts gian lận (copy/paste/right-click) của NV khi làm bài kiểm tra nội quy.
// Filter theo ngày, NV, loại action. Stats top NV + top action.

import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api } from '../lib/api';
import { useAuthStore } from '../lib/auth.js';

const ACTION_META = {
  copy_question:            { label: 'Copy câu hỏi',     icon: '📋', color: 'amber' },
  copy_correct_answer:      { label: 'Copy đáp án',      icon: '🔓', color: 'rose'  },
  paste_answer:             { label: 'Paste vào ô trả lời', icon: '📥', color: 'red' },
  right_click_question:     { label: 'Chuột phải vào câu hỏi', icon: '🖱', color: 'slate' },
  // M73 (2026-04-25): chặn copy body chương — tránh leak nội quy
  copy_chapter_body:        { label: 'Copy nội dung chương', icon: '📄', color: 'amber' },
  right_click_chapter_body: { label: 'Chuột phải vào chương', icon: '🖱', color: 'slate' },
  // M290 (2026-05-25): action từ examService.recordPasteBlocked
  exam_paste_blocked:       { label: 'Paste bị chặn (bài kiểm tra)', icon: '🚫', color: 'red' },
  // M328 (2026-07-31): exam_tab_hidden ĐÃ GỠ khỏi cheat-log (chuyển tab khi thi
  // là việc bình thường — NV lên đơn/trả lời khách). BE cũng bỏ khỏi pattern.
  // M326 (2026-05-30): dùng Hỏi đáp khi cửa hàng đang có người thi
  exam_ask_self:            { label: 'Tự tra Hỏi đáp khi đang thi', icon: '🔍', color: 'red' },
  exam_ask_other:           { label: 'Tra hộ Hỏi đáp (có người đang thi)', icon: '🤝', color: 'red' },
};
const COLOR_BG = {
  amber: 'bg-amber-100 text-amber-800 border-amber-300',
  rose:  'bg-rose-100 text-rose-800 border-rose-300',
  red:   'bg-red-100 text-red-800 border-red-300',
  slate: 'bg-slate-100 text-slate-700 border-slate-300',
};

function fmtTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('vi-VN', {
      day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

export default function AdminCheatLogPage() {
  const nav = useNavigate();
  const { user } = useAuthStore();
  // M146 (2026-05-12): manager với quyền 'cheat_log' cũng truy cập được
  const isAdmin = (function () {
    if (user?.role === 'admin') return true;
    if (user?.role === 'manager' && Array.isArray(user?.permissions)) return user.permissions.includes('cheat_log');
    return false;
  })();

  // Filters
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [action, setAction] = useState('');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const PAGE = 100;
  const [offset, setOffset] = useState(0);

  // M320: methods (phương pháp chống gian lận đang áp dụng)
  const [methods, setMethods] = useState(null);
  const [showMethods, setShowMethods] = useState(false);

  useEffect(() => {
    if (!isAdmin) {
      toast.error('Chỉ admin được truy cập');
      nav('/', { replace: true });
    }
  }, [isAdmin, nav]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.adminRulesCheatLog({
        from: from || undefined,
        to: to || undefined,
        employee_id: employeeId || undefined,
        action: action || undefined,
        limit: PAGE,
        offset,
      });
      setData(res);
    } catch (e) {
      toast.error('Lỗi tải log: ' + (e?.data?.error || e?.message));
    } finally {
      setLoading(false);
    }
  }, [from, to, employeeId, action, offset]);

  useEffect(() => { load(); }, [load]);

  // M320: load methods 1 lần khi mount
  useEffect(() => {
    if (!isAdmin) return;
    api.adminCheatLogMethods()
      .then(setMethods)
      .catch(() => { /* silent — section sẽ ẩn */ });
  }, [isAdmin]);

  function clearFilters() {
    setFrom(''); setTo(''); setEmployeeId(''); setAction(''); setOffset(0);
  }

  function exportCsv() {
    if (!data?.items?.length) return;
    const headers = ['Thời gian', 'NV', 'Hành vi', 'Chương', 'IP', 'Question ID'];
    const rows = data.items.map(i => [
      fmtTime(i.created_at),
      i.employee_name || '(unknown)',
      ACTION_META[i.action_short]?.label || i.action_short,
      i.module_title || `module#${i.module_id}`,
      i.ip || '',
      i.meta?.question_id || '',
    ]);
    const csv = [headers, ...rows]
      .map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cheat-log-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (loading && !data) {
    return (
      <div className="min-h-full bg-slate-50 py-8 px-4">
        <div className="max-w-7xl mx-auto text-center text-sm text-slate-500">Đang tải…</div>
      </div>
    );
  }

  const items = data?.items || [];
  const total = data?.total || 0;
  const stats = data?.stats || { top_employees: [], top_actions: [] };

  return (
    <div className="min-h-full bg-gradient-to-br from-rose-50/40 via-white to-amber-50/40 py-8 px-4 md:px-6">
      <div className="max-w-7xl mx-auto">
        {/* Back */}
        <Link to="/admin" className="text-sm text-slate-500 hover:text-slate-700 inline-block mb-4">← Trang admin</Link>

        {/* Header */}
        <header className="mb-6 bg-white rounded-2xl border border-rose-100 shadow-sm p-5 flex items-center gap-4 flex-wrap">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-rose-500 to-amber-500 text-white flex items-center justify-center text-2xl shadow-md">
            🚨
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl font-bold text-slate-800">Log gian lận nội quy</h1>
            <p className="text-xs text-slate-500 mt-0.5">
              NV cố tình copy câu hỏi / paste vào ô trả lời / right-click → log đầy đủ tại đây.
            </p>
          </div>
          <div className="text-right">
            <div className="text-xs text-slate-500">Tổng attempts</div>
            <div className="text-2xl font-bold text-rose-600 tabular-nums">{total}</div>
          </div>
        </header>

        {/* M320: Phương pháp chống gian lận đang áp dụng */}
        {methods && methods.methods?.length > 0 && (
          <section className="mb-6 bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
            <div className="flex items-start justify-between gap-3 mb-1">
              <button
                type="button"
                onClick={() => setShowMethods(s => !s)}
                data-testid="cheat-log-methods-toggle"
                className="flex-1 flex items-center justify-between text-left"
              >
                <div>
                  <h2 className="text-sm font-semibold text-slate-800">
                    🛡️ {methods.title} ({methods.methods.length})
                  </h2>
                  <p className="text-xs text-slate-500 mt-1">
                    Cập nhật {methods.updated_at} · Click để {showMethods ? 'ẩn' : 'xem'} chi tiết.
                  </p>
                </div>
                <span className="text-slate-400 text-lg mr-3">{showMethods ? '▲' : '▼'}</span>
              </button>
            </div>
            {showMethods && (
              <div className="mt-4 space-y-3">
                {methods.methods.map((m) => (
                  <div key={m.id} className="border-l-4 border-emerald-300 pl-3 py-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-slate-800">{m.name}</span>
                      <span className="text-[11px] bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded">
                        ✓ {m.status === 'active' ? 'Đang áp dụng' : m.status}
                      </span>
                      <span className="text-[11px] text-slate-400">· {m.since}</span>
                    </div>
                    <div className="text-xs text-slate-600 mt-1">
                      <b>Chống:</b> {m.covers}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      <b>Cách làm:</b> {m.how}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Stats — top NV + top action */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
            <h3 className="text-sm font-bold text-slate-700 mb-2 flex items-center gap-1.5">
              👥 Top NV bị log nhiều nhất
            </h3>
            {stats.top_employees.length === 0 ? (
              <div className="text-xs text-slate-400 italic py-2">Chưa có NV nào bị log</div>
            ) : (
              <div className="space-y-1">
                {stats.top_employees.map((e, i) => (
                  <button
                    key={e.actor_employee_id}
                    onClick={() => { setEmployeeId(String(e.actor_employee_id)); setOffset(0); }}
                    className="w-full flex items-center gap-3 px-2 py-1.5 rounded-lg hover:bg-rose-50 transition text-left"
                  >
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      i === 0 ? 'bg-rose-200 text-rose-800' :
                      i === 1 ? 'bg-amber-200 text-amber-800' :
                      i === 2 ? 'bg-yellow-100 text-yellow-700' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      {i + 1}
                    </span>
                    <span className="flex-1 text-sm text-slate-700 truncate">{e.full_name || `Employee #${e.actor_employee_id}`}</span>
                    <span className="text-sm font-bold text-rose-600 tabular-nums">{e.cnt}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
            <h3 className="text-sm font-bold text-slate-700 mb-2 flex items-center gap-1.5">
              📊 Top hành vi vi phạm
            </h3>
            {stats.top_actions.length === 0 ? (
              <div className="text-xs text-slate-400 italic py-2">Chưa có hành vi nào</div>
            ) : (
              <div className="space-y-1">
                {stats.top_actions.map((a) => {
                  const meta = ACTION_META[a.action] || { label: a.action, icon: '?', color: 'slate' };
                  return (
                    <button
                      key={a.action}
                      onClick={() => { setAction(a.action); setOffset(0); }}
                      className="w-full flex items-center gap-3 px-2 py-1.5 rounded-lg hover:bg-slate-50 transition text-left"
                    >
                      <span className="text-lg">{meta.icon}</span>
                      <span className="flex-1 text-sm text-slate-700">{meta.label}</span>
                      <span className="text-sm font-bold text-slate-600 tabular-nums">{a.cnt}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm mb-4">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <div className="flex items-center gap-2 text-xs text-slate-600">
              <span className="font-semibold">📅 Từ:</span>
              <input type="date" value={from} onChange={e => { setFrom(e.target.value); setOffset(0); }}
                     className="border border-slate-300 rounded px-2 py-1 text-xs" />
              <span className="font-semibold">đến:</span>
              <input type="date" value={to} onChange={e => { setTo(e.target.value); setOffset(0); }}
                     className="border border-slate-300 rounded px-2 py-1 text-xs" />
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-600">
              <span className="font-semibold">🎯 Hành vi:</span>
              <select value={action} onChange={e => { setAction(e.target.value); setOffset(0); }}
                      className="border border-slate-300 rounded px-2 py-1 text-xs bg-white">
                <option value="">Tất cả</option>
                {Object.entries(ACTION_META).map(([k, v]) => (
                  <option key={k} value={k}>{v.icon} {v.label}</option>
                ))}
              </select>
            </div>
            {employeeId && (
              <button onClick={() => { setEmployeeId(''); setOffset(0); }}
                      className="text-xs px-2 py-1 rounded-full bg-rose-100 text-rose-700 hover:bg-rose-200">
                NV#{employeeId} ✕
              </button>
            )}
            <button onClick={clearFilters}
                    className="text-xs text-slate-500 hover:text-slate-800 underline">
              Xoá filter
            </button>
            <div className="ml-auto flex items-center gap-2">
              <span className="text-xs text-slate-500">{items.length} / {total} kết quả</span>
              <button onClick={exportCsv} disabled={items.length === 0}
                      className="text-xs px-3 py-1 rounded bg-emerald-50 border border-emerald-300 text-emerald-700 hover:bg-emerald-100 disabled:opacity-40">
                📤 Export CSV
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          {items.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-400 italic">
              {loading ? 'Đang tải…' : 'Không có log nào trong khoảng thời gian này.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase text-slate-600">
                  <tr>
                    <th className="px-3 py-2 text-left">Thời gian</th>
                    <th className="px-3 py-2 text-left">NV</th>
                    <th className="px-3 py-2 text-left">Hành vi</th>
                    <th className="px-3 py-2 text-left">Chương</th>
                    <th className="px-3 py-2 text-left">Câu Q</th>
                    <th className="px-3 py-2 text-left">IP</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {items.map((r) => {
                    const meta = ACTION_META[r.action_short] || { label: r.action_short, icon: '?', color: 'slate' };
                    return (
                      <tr key={r.id} className="hover:bg-slate-50">
                        <td className="px-3 py-2 text-xs text-slate-500 tabular-nums whitespace-nowrap">
                          {fmtTime(r.created_at)}
                        </td>
                        <td className="px-3 py-2">
                          {r.employee_name ? (
                            <button
                              onClick={() => { setEmployeeId(String(r.actor_employee_id)); setOffset(0); }}
                              className="text-sky-700 hover:underline font-medium"
                            >
                              👤 {r.employee_name}
                            </button>
                          ) : (
                            <span className="text-slate-400 italic">(không rõ)</span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${COLOR_BG[meta.color]}`}>
                            <span>{meta.icon}</span>
                            <span>{meta.label}</span>
                          </span>
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-600 max-w-xs truncate" title={r.module_title}>
                          {r.module_title ? `📖 ${r.module_title}` : `module#${r.module_id}`}
                        </td>
                        <td className="px-3 py-2 text-xs font-mono text-slate-500">
                          {r.meta?.question_id ? `#${r.meta.question_id}` : '—'}
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-400 font-mono">{r.ip || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {total > PAGE && (
          <div className="mt-4 flex items-center justify-center gap-2 text-sm">
            <button onClick={() => setOffset(Math.max(0, offset - PAGE))} disabled={offset === 0}
                    className="px-3 py-1.5 rounded border border-slate-300 disabled:opacity-40 hover:bg-slate-50">
              ← Trước
            </button>
            <span className="text-slate-500">
              Trang {Math.floor(offset / PAGE) + 1} / {Math.ceil(total / PAGE)}
            </span>
            <button onClick={() => setOffset(offset + PAGE)} disabled={offset + PAGE >= total}
                    className="px-3 py-1.5 rounded border border-slate-300 disabled:opacity-40 hover:bg-slate-50">
              Sau →
            </button>
          </div>
        )}

        <div className="mt-6 text-center text-xs text-slate-400">
          💡 Click vào tên NV hoặc tên hành vi ở stats để filter nhanh.
        </div>
      </div>

    </div>
  );
}
